import { useState, useMemo } from 'react';
import { actionOptions } from './action-options';
import PanelSectionHeader, {
  SummaryPanelVariant,
} from '../../../shared/panel-section-header';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import PlayArrow from '@mui/icons-material/PlayArrow';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import AddToDrive from '@mui/icons-material/AddToDrive';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

interface RulesSummaryThenSectionProps {
  actionTodo: string[];
  variant?: SummaryPanelVariant;
}

export default function RuleSummaryThenSection({
  actionTodo = [],
  variant = 'full',
}: RulesSummaryThenSectionProps) {
  const theme = useTheme();
  const isCompact = variant === 'compact';
  const [thenExpanded, setThenExpanded] = useState(false);

  const showActions = actionTodo && actionTodo.length > 0;

  const visibleActionItems = useMemo(() => {
    if (!actionTodo) return [];
    return thenExpanded ? actionTodo : actionTodo?.slice(0, 2);
  }, [actionTodo, thenExpanded]);

  const collapsedThenCount = useMemo(() => {
    if (!actionTodo) return 0;
    return Math.max(0, actionTodo?.length - 2);
  }, [actionTodo]);

  const getActionIcon = (actionValue: string): React.ReactNode => {
    const actionOpt = actionOptions.find((o) => o.value === actionValue);
    const actionType = actionOpt?.type;
    switch (actionType) {
      case 'External':
        return (
          <EmailOutlined
            sx={{ fontSize: 16, color: theme.palette.semantic.icon.error }}
          />
        );
      case 'Policy':
        return (
          <DescriptionOutlined
            sx={{ fontSize: 16, color: theme.palette.semantic.icon.blue }}
          />
        );
      case 'User':
      case 'Actor':
        return (
          <PersonOutlined
            sx={{ fontSize: 16, color: theme.palette.semantic.icon.green }}
          />
        );
      case 'Resource Calendar':
        return (
          <CalendarTodayOutlined
            sx={{ fontSize: 16, color: theme.palette.semantic.icon.green }}
          />
        );
      case 'Drive':
        return (
          <AddToDrive
            sx={{ fontSize: 16, color: theme.palette.semantic.icon.green }}
          />
        );
      default:
        return (
          <PersonOutlined
            sx={{ fontSize: 16, color: theme.palette.semantic.icon.green }}
          />
        );
    }
  };

  const renderActionItems = () => {
    return visibleActionItems.map((actionValue) => {
      const actionOpt = actionOptions.find((o) => o.value === actionValue);
      const displayValue = actionOpt?.displayValue ?? actionValue;

      return (
        <Box
          key={actionValue}
          sx={{
            border: '0.5px solid',
            borderColor: theme.palette.semantic.outline.light,
            borderRadius: 1,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: theme.palette.semantic.surface.bg1,
          }}
        >
          {getActionIcon(actionValue)}
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {displayValue}
          </Typography>
        </Box>
      );
    });
  };

  return (
    <Box
      sx={{
        my: 2,
      }}
    >
      <PanelSectionHeader variant={variant} title="THEN" icon={PlayArrow} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          ...(!isCompact && {
            backgroundColor: theme.palette.semantic.surface.bg3,
            p: 2,
            border: '1px solid',
            borderColor: theme.palette.semantic.outline.light,
          }),
        }}
      >
        {showActions ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              px: 0.5,
            }}
          >
            {renderActionItems()}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', px: 1 }}>
            <Typography variant="body2">—</Typography>
          </Box>
        )}

        {collapsedThenCount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => setThenExpanded(!thenExpanded)}
              startIcon={thenExpanded ? <ExpandLess /> : <ExpandMore />}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 500,
                p: 0.5,
                color: theme.palette.semantic.text.support,
                '&:hover': {
                  bgcolor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              {thenExpanded
                ? 'show less'
                : `show ${collapsedThenCount} more action${
                    collapsedThenCount > 1 ? 's' : ''
                  }`}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
